/**
 * SafeImage — calm <img> wrapper with idempotent placeholder fallback.
 *
 * On image-load error, swaps to an inline-SVG calm placeholder tile
 * (warm or sage variant) and sets a brand-voice alt text. Idempotent:
 * a dataset guard prevents an error-loop if even the placeholder fails.
 *
 * Generalizes the iter39 Gallery onError fix into a single shared component
 * adopted across Gallery, Moments, ChildProfile, Messages, and any future
 * photo surface. Drop-in replacement for <img>: same props.
 */
import { forwardRef } from "react";

// Two calm placeholder variants — pick whichever harmonizes with the host
// surface. Sage works on cream/white backgrounds; warm works on photo grids.
const PLACEHOLDERS = {
  sage:
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='%23F2EFEA'/><text x='50' y='54' text-anchor='middle' font-family='serif' font-style='italic' font-size='10' fill='%238A9984'>·</text></svg>"
    ),
  warm:
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect width='100' height='100' fill='%23F4EDDF'/><text x='50' y='54' text-anchor='middle' font-family='serif' font-style='italic' font-size='10' fill='%23B58A3F'>·</text></svg>"
    ),
};

const SafeImage = forwardRef(function SafeImage(
  { fallbackVariant = "sage", fallbackAlt = "Photo unavailable", onError, ...props },
  ref
) {
  return (
    <img
      ref={ref}
      {...props}
      onError={(e) => {
        const img = e.currentTarget;
        if (img.dataset.fallback === "1") return;
        img.dataset.fallback = "1";
        img.src = PLACEHOLDERS[fallbackVariant] || PLACEHOLDERS.sage;
        img.alt = fallbackAlt;
        if (onError) onError(e);
      }}
    />
  );
});

export default SafeImage;
